// web/src/api/index.js
// Using the '@api' alias for importing API modules
import BanksAPI from '@/api/banksCenter/BanksAPI';
import HAAdminAPI from '@/api/haAdmin/HAAdminAPI';
import HedgeAccountingOpsAPI from '@/api/hedgeAccountingOps/HedgeAccountingOpsAPI';
import PublicHolidayCalendarAPI from '@/api/publicHolidayCalendar/PublicHolidayCalendarAPI';
import AuthAPI from '@api/auth/AuthAPI';
import CompanyEntityAPI from '@api/companyCenter/CompanyEntityAPI';
import CurrencyAPI from '@api/currency/CurrencyAPI';
import DashboardAPI from '@api/dashboard/DashboardAPI';
import FiltersAPI from '@api/filters/FiltersAPI';
import GVOpportunitiesAPI from '@api/gvOportunities/GVOpportunitiesAPI';
import HedgeAccountingAPI from '@api/hedgeAccounting/HedgeAccountingAPI';
import MarketDataAPI from '@api/marketData/MarketDataAPI';
import PermissionsAPI from '@api/permissions/PermissionsAPI';
import ScheduledDeliverablesAPI from '@api/scheduledDeliverables/ScheduledDeliverablesAPI';
import SupportAPI from '@api/support/SupportAPI';
import TermsAPI from '@api/terms/termsAPI';
import TradeCenterAPI from '@api/tradeCenter/TradeCenterAPI';
import TransactionsAPI from '@api/transactions/transactionsAPI';
import UploadApi from '@api/upload/UploadApi';
import UsersAPI from '@api/users/UsersAPI';
import UtilityAPI from '@api/utility/UtilityAPI';
import WidgetAPI from '@api/widget/WidgetAPI';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { APIInterface, ApiConfig, GeneralState } from '../types/api';

import ComplianceCentreAPI from './complianceCenter/complianceCentreAPI';
import DealContingentAPI from './dealContingentAnalysis/DealContingentAPI';
import DealPricingUpdatesAPI from './dealPricingUpdates/DealPricingUpdatesAPI';
import DealsAPI from './deals/DealsAPI';
import DebtAPI from './debt/DebtAPI';
import DebtPricerAPI from './debtPricer/DebtPricerAPI';
import FileManagementAPI from './fileManagement/FileManagementAPI';
import FundLevelHedgingAPI from './fundLevel/fundLevelHedgingAPI';
import TradeNoticationAPI from './fundLevel/tradeNotificationAPI';
import FundLevelAdminAPI from './fundLevelAdmin/fundLevelAdminAPI';
import TransactionRollingAPI from './fundLevelRoll/transactionRollingAPI';
import FxAnalysisAPI from './fxAnalysis/FXAnalysisAPI';
import JournalEntriesAPI from './hedgeAccounting/journalEntriesAPI';
import IRAnalysisAPI from './irAnalysis/IRAnalysisAPI';
import PricerAPI from './pricer/PricerAPI';
import ReportCenterAPI from './reportCenter/ReportCenterAPI';
import ReportAPI from './reporting/reportAPI';
import RevalueTradesAPI from './revalueTrades/RevalueTradesAPI';
import RunDeliverablesAPI from './runDeliverables/RunDeliverablesAPI';
import SettlementAPI from './settlements/SettlementsAPI';
import SimulationAPI from './simulation/SimulationAPI';
import SponsorDashboardAPI from './sponsorDashboard/SponsorDashboardAPI';
import ValidationDashboardAPI from './validationDashboard/ValidationDashboardAPI';

const apiConfig: ApiConfig = {
  baseURL: import.meta.env.VITE_REACT_APP_BACKEND_URL,
  debug: import.meta.env.VITE_REACT_APP_API_DEBUG === 'true',
};

export const generalState: GeneralState = {
  loading: false,
  data: null,
  error: null,
  successMessage: null,
  errorMessage: null,
};

export class API implements APIInterface {
  debug: boolean;
  client: AxiosInstance;
  auth: AuthAPI;
  dashboard: DashboardAPI;
  widget: WidgetAPI;
  currency: CurrencyAPI;
  permissions: PermissionsAPI;
  marketData: MarketDataAPI;
  utility: UtilityAPI;
  filters: FiltersAPI;
  transactions: TransactionsAPI;
  journalEntries: JournalEntriesAPI;
  tradeCenter: TradeCenterAPI;
  users: UsersAPI;
  terms: TermsAPI;
  settlements: SettlementAPI;
  sponsor: SponsorDashboardAPI;
  reportCenter: ReportCenterAPI;
  hedgeAccounting: HedgeAccountingAPI;
  runDeliverables: RunDeliverablesAPI;
  scheduledDeliverables: ScheduledDeliverablesAPI;
  companyEntity: CompanyEntityAPI;
  upload: UploadApi;
  revalueTrades: RevalueTradesAPI;
  pricer: PricerAPI;
  debtPricer: DebtPricerAPI;
  tradeNotification: TradeNoticationAPI;
  reports: ReportAPI;
  fundLevelAdmin: FundLevelAdminAPI;
  fundLevelHedging: FundLevelHedgingAPI;
  deals: DealsAPI;
  dealPricingUpdates: DealPricingUpdatesAPI;
  irAnalysis: IRAnalysisAPI;
  complianceFL: ComplianceCentreAPI;
  banks: BanksAPI;
  fileManagement: FileManagementAPI;
  transactionRolling: TransactionRollingAPI;
  fxAnalysis: FxAnalysisAPI;
  dealContingent: DealContingentAPI;
  debt: DebtAPI;
  haAdmin: HAAdminAPI;
  simulation: SimulationAPI;
  haOps: HedgeAccountingOpsAPI;
  validationDashboard: ValidationDashboardAPI;
  gvOpportunities: GVOpportunitiesAPI;
  publicHolidayCalendar: PublicHolidayCalendarAPI;
  support: SupportAPI;

  constructor() {
    this.debug = apiConfig.debug;

    this.client = axios.create({
      baseURL: apiConfig.baseURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Viper-Version': '2',
      },
    });

    // https://stackoverflow.com/questions/69002516/axios-not-sending-xsrf-token-with-headers
    // https://github.com/axios/axios/pull/6046#issue-1968991555
    // So now the user must explicitly set withXSRFToken to true to send XSRF token to third-party origins.
    // By default withXSRFToken is undefined - the token will be sent only to the same origin.
    this.client.defaults.withXSRFToken = true;
    this.client.defaults.xsrfCookieName = 'csrftoken';
    this.client.defaults.xsrfHeaderName = 'X-CSRFToken';

    this.auth = new AuthAPI(this.client);
    this.dashboard = new DashboardAPI(this.client);
    this.widget = new WidgetAPI(this.client);
    this.currency = new CurrencyAPI(this.client);
    this.permissions = new PermissionsAPI(this.client);
    this.marketData = new MarketDataAPI(this.client);
    this.utility = new UtilityAPI(this.client);
    this.filters = new FiltersAPI(this.client);
    this.transactions = new TransactionsAPI(this.client);
    this.journalEntries = new JournalEntriesAPI(this.client);
    this.tradeCenter = new TradeCenterAPI(this.client);
    this.users = new UsersAPI(this.client);
    this.terms = new TermsAPI(this.client);
    this.settlements = new SettlementAPI(this.client);
    this.sponsor = new SponsorDashboardAPI(this.client);
    this.reportCenter = new ReportCenterAPI(this.client);
    this.hedgeAccounting = new HedgeAccountingAPI(this.client);
    this.runDeliverables = new RunDeliverablesAPI(this.client);
    this.scheduledDeliverables = new ScheduledDeliverablesAPI(this.client);
    this.companyEntity = new CompanyEntityAPI(this.client);
    this.upload = new UploadApi(this.client);
    this.revalueTrades = new RevalueTradesAPI(this.client);
    this.pricer = new PricerAPI(this.client);
    this.debtPricer = new DebtPricerAPI(this.client);
    this.tradeNotification = new TradeNoticationAPI(this.client);
    this.reports = new ReportAPI(this.client);
    this.fundLevelAdmin = new FundLevelAdminAPI(this.client);
    this.fundLevelHedging = new FundLevelHedgingAPI(this.client);
    this.deals = new DealsAPI(this.client);
    this.dealPricingUpdates = new DealPricingUpdatesAPI(this.client);
    this.irAnalysis = new IRAnalysisAPI(this.client);
    this.complianceFL = new ComplianceCentreAPI(this.client);
    this.banks = new BanksAPI(this.client);
    this.fileManagement = new FileManagementAPI(this.client);
    this.transactionRolling = new TransactionRollingAPI(this.client);
    this.fxAnalysis = new FxAnalysisAPI(this.client);
    this.dealContingent = new DealContingentAPI(this.client);
    this.debt = new DebtAPI(this.client);
    this.haAdmin = new HAAdminAPI(this.client);
    this.simulation = new SimulationAPI(this.client);
    this.haOps = new HedgeAccountingOpsAPI(this.client);
    this.validationDashboard = new ValidationDashboardAPI(this.client);
    this.gvOpportunities = new GVOpportunitiesAPI(this.client);
    this.publicHolidayCalendar = new PublicHolidayCalendarAPI(this.client);
    this.support = new SupportAPI(this.client);
  }

  logRequest(request: AxiosRequestConfig) {
    console.log(
      `API Request: ${request.method?.toUpperCase()} ${request.url}`,
      request,
    );
  }

  handleSuccessResponse(response: AxiosResponse): AxiosResponse {
    return response;
  }

  handleErrorResponse(error: AxiosError): Promise<AxiosError> {
    return Promise.reject(error);
  }
}

const apiInstance = new API();
export default apiInstance;
